import React from 'react';

import { ScrollView } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataProviderReadDto from '../../classes/dtos/pathcommonmodule/DataProviderReadDto';
import PathSynchroSetupDto from '../../classes/dtos/pathsynchromodule/PathSynchroSetupDto';
import ScheduleDto from '../../classes/dtos/schedulermodule/ScheduleDto';

export interface SynchroFieldResume {
    SourceLabel: string;
    TargetLabel: string;
    IsKey: boolean;
    /** Label of the transformation applied to the field; empty when the value is copied as is. */
    TransformationLabel: string;
}

interface SynchroResumeProps {
    Source: DataTableReadDto | null;
    Target: DataTableReadDto | null;
    Fields: SynchroFieldResume[];
    Setup: PathSynchroSetupDto | null;
    Schedule: ScheduleDto | null;
}

const GroupStyle: React.CSSProperties = { paddingTop: 12, paddingBottom: 16, paddingLeft: 16, paddingRight: 16, background: '#1A1A20', borderRadius: 16, border: '1px #3D3D46 solid', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'inline-flex' };
const GroupTitleStyle: React.CSSProperties = { color: '#E3E3E8', fontSize: 12, fontFamily: 'Manrope', fontWeight: 600, lineHeight: '15.60px', letterSpacing: 0.24 };
const CardStyle: React.CSSProperties = { alignSelf: 'stretch', minHeight: 79, padding: 16, background: '#202028', borderRadius: 10, border: '1px #3D3D46 solid', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 10, display: 'flex' };
const CaptionStyle: React.CSSProperties = { color: '#9393A2', fontSize: 10, fontFamily: 'Manrope', fontWeight: 500, textTransform: 'uppercase', lineHeight: '13px', letterSpacing: 1 };
const ValueStyle: React.CSSProperties = { color: '#E3E3E8', fontSize: 16, fontFamily: 'Manrope', fontWeight: 500, lineHeight: '24px', letterSpacing: 0.32 };
const FieldStyle: React.CSSProperties = { alignSelf: 'stretch', padding: '4px 8px', background: '#5A5A65', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 10, minWidth: 150, color: '#E3E3E8', fontSize: 14, fontFamily: 'Manrope', fontWeight: 500, lineHeight: '21px', letterSpacing: 0.28 };

const Separator: React.FC<{ Text: string }> = ({ Text }) => (
    <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'flex' }}>
        <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
        <div style={{ ...CaptionStyle, fontWeight: 600 }}>{Text}</div>
        <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
    </div>
);

const Link: React.FC = () => <div style={{ width: 1, height: 32, border: '1px #3D3D46 dotted' }}></div>;

const Card: React.FC<{ Caption: string, Value: string }> = ({ Caption, Value }) => (
    <div style={CardStyle}>
        <div style={CaptionStyle}>{Caption}</div>
        <div style={ValueStyle}>{Value}</div>
    </div>
);

const DataTableGroup: React.FC<{ Title: string, DataTable: DataTableReadDto | null }> = ({ Title, DataTable }) => {
    const dataProvider = new DataProviderReadDto(DataTable);

    return (
        <div style={GroupStyle}>
            <div style={{ alignSelf: 'stretch', padding: '6px 10px', display: 'flex', justifyContent: 'center' }}>
                <div style={GroupTitleStyle}>{Title}</div>
            </div>
            <div style={{ flexDirection: 'column', alignItems: 'center', display: 'flex', alignSelf: 'stretch' }}>
                <Card Caption={formatMessage("SynchroResume_Provider")} Value={dataProvider.Label ?? ""} />
                {dataProvider.WithDatabase && <Link />}
                {dataProvider.WithDatabase && <Card Caption={formatMessage("SynchroResume_Database")} Value={dataProvider.DataSourceDataTableRead?.DatabaseReferenceLabel ?? ""} />}
                {dataProvider.WithTable && <Link />}
                {dataProvider.WithTable && <Card Caption={formatMessage("SynchroResume_Table")} Value={dataProvider.DataSourceDataTableRead?.TableReferenceLabel ?? ""} />}
            </div>
        </div>
    );
};

const Flag: React.FC<{ Label: string, Active: boolean }> = ({ Label, Active }) => (
    <div style={{ ...FieldStyle, background: Active ? '#2E6B4F' : '#3D3D46', opacity: Active ? 1 : 0.7 }}>
        <i className={`dx-icon dx-icon-${Active ? 'check' : 'close'}`} style={{ fontSize: 14 }}></i>
        <span>{Label}</span>
    </div>
);

/**
 * Read-only recap of the synchro path (same role as the Resume tab of the other path editors):
 * source -> target, the mapped fields, then how the synchronization behaves. Running it lives in the Execution tab.
 */
const SynchroResume: React.FC<SynchroResumeProps> = ({ Source, Target, Fields, Setup, Schedule }) => {
    return (
        <ScrollView style={{ width: '100%', height: 'calc(100vh - 320px)' }}>
            <div style={{ width: '100%', minHeight: 'calc(100vh - 320px)', padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DataTableGroup Title={formatMessage("SynchroResume_SourceTitle")} DataTable={Source} />
                    <Separator Text={formatMessage("SynchroResume_FieldsSeparator")} />
                    <div style={GroupStyle}>
                        <div style={{ alignSelf: 'stretch', padding: '6px 10px', display: 'flex', justifyContent: 'center' }}>
                            <div style={GroupTitleStyle}>{formatMessage("SynchroResume_MappingTitle")}</div>
                        </div>
                        <div style={{ ...CardStyle, minHeight: 0 }}>
                            <div style={CaptionStyle}>{formatMessage("SynchroResume_MappedFields")}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'max-content minmax(150px, max-content) minmax(150px, max-content) minmax(150px, max-content)', gap: 4, alignSelf: 'stretch' }}>
                                <div style={{ ...CaptionStyle, textAlign: 'center' }}>{formatMessage("SynchroResume_Key")}</div>
                                <div style={CaptionStyle}>{formatMessage("SynchroResume_SourceTitle")}</div>
                                <div style={CaptionStyle}>{formatMessage("SynchroResume_Transformation")}</div>
                                <div style={CaptionStyle}>{formatMessage("SynchroResume_TargetTitle")}</div>
                                {Fields.map((field, i) => (
                                    <React.Fragment key={i}>
                                        <div style={{ ...FieldStyle, minWidth: 0, justifyContent: 'center' }}>
                                            {field.IsKey && <i className="dx-icon dx-icon-key" style={{ fontSize: 14 }}></i>}
                                        </div>
                                        <div style={FieldStyle}>{field.SourceLabel !== "" ? field.SourceLabel : formatMessage("SynchroResume_NoSource")}</div>
                                        <div style={{ ...FieldStyle, minWidth: 0 }}>
                                            {field.TransformationLabel !== "" && <i className="dx-icon dx-icon-func" style={{ fontSize: 14 }}></i>}
                                            <span>{field.TransformationLabel}</span>
                                        </div>
                                        <div style={FieldStyle}>{field.TargetLabel}</div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                    <Separator Text={formatMessage("SynchroResume_FieldsSeparator")} />
                    <DataTableGroup Title={formatMessage("SynchroResume_TargetTitle")} DataTable={Target} />
                    <div style={{ width: 16 }}></div>
                    <div style={GroupStyle}>
                        <div style={{ alignSelf: 'stretch', padding: '6px 10px', display: 'flex', justifyContent: 'center' }}>
                            <div style={GroupTitleStyle}>{formatMessage("SynchroResume_SetupTitle")}</div>
                        </div>
                        <div style={{ ...CardStyle, minHeight: 0 }}>
                            <Flag Label={formatMessage("SynchroResume_Addition")} Active={Setup?.AdditionActivated ?? false} />
                            <Flag Label={formatMessage("SynchroResume_Update")} Active={Setup?.UpdateActivated ?? false} />
                            <Flag Label={formatMessage("SynchroResume_Deletion")} Active={Setup?.DeletionActivated ?? false} />
                            <Flag Label={formatMessage("SynchroResume_Scheduled")} Active={Schedule?.IsActive ?? false} />
                        </div>
                    </div>
                </div>
            </div>
        </ScrollView>
    );
};

export default SynchroResume;
